import React, {createContext, useEffect, useState, useRef, useCallback} from 'react';
import {getConfig, PRISMAX_BACKEND_URL} from "../configs/config";
import FirstTimeModal from "./FirstTimeModal";

export const SwapMainAppContext = createContext(null);

export function SwapMainAppProvider({children}) {
    const [isContextReady, setIsContextReady] = useState(false);
    const [notification, setNotification] = useState({
        status: null,
        message: null,
    });
    const notificationTimerRef = useRef(null);
    const [shrinkLeftPanel, setShrinkLeftPanel] = useState(false);
    //connect panel
    const [showPanel, setShowPanel] = useState(false);

    // Add transaction-related state
    const [userClass, setUserClass] = useState(() => {
        return localStorage.getItem('userClass') || 'Explorer Member';
    });
    const [showFirstTimeModal, setShowFirstTimeModal] = useState(false);

    const [selectedToken, setSelectedToken] = useState('USDT');
    const [amount, setAmount] = useState('');
    const [transactionTrigger, setTransactionTrigger] = useState(0);
    const [onMembershipPaymentSuccess, setOnMembershipPaymentSuccess] = useState(() => () => {
        console.log("onMembershipPaymentSuccess default no-op called");
    });
    const [isProcessing, setIsProcessing] = useState(false);

    const [usdtBalance, setUsdtBalance] = useState(0);
    const [usdcBalance, setUsdcBalance] = useState(0);
    const [gatewayToken, setGatewayToken] = useState(
        localStorage.getItem('gatewayToken') || ''
    );
    const [sessionUrlKey, setSessionUrlKey] = useState('')
    const [pointsRefreshTrigger, setPointsRefreshTrigger] = useState(0);

    const [usdtTokenAddress, setUsdtTokenAddress] = useState('');
    const [usdcTokenAddress, setUsdcTokenAddress] = useState('');
    const [prismaxRecevingAddress, setPrismaxRecevingAddress] = useState('');

    const [walletAddress, setWalletAddress] = useState(() => {
        return localStorage.getItem('walletAddress') || '';
    });
    const [userEmail, setUserEmail] = useState(() => {
        return localStorage.getItem('userEmail') || '';
    });
    const [userId, setUserId] = useState(() => {
        const storedUserId = localStorage.getItem('userId');
        return storedUserId ? parseInt(storedUserId, 10) : null;
    });

    // for error message
    function showNotification(status, message) {
        if (notificationTimerRef.current) {
            clearTimeout(notificationTimerRef.current);
            notificationTimerRef.current = null;
        }

        setNotification({status, message});

        notificationTimerRef.current = setTimeout(() => {
            setNotification({status: null, message: null});
            notificationTimerRef.current = null;
        }, 5000);
    }


    useEffect(() => {
        return () => {
            if (notificationTimerRef.current) {
                clearTimeout(notificationTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const initializeContext = async () => {
            try {
                const configData = await getConfig();
                setUsdtTokenAddress(configData.usdtTokenAddress || '');
                setUsdcTokenAddress(configData.usdcTokenAddress || '');
                setPrismaxRecevingAddress(configData.prismaxRecevingAddress || '');

                if (userEmail) {
                    await fetchUserInfoAfterConnect();
                }

                setIsContextReady(true);
            } catch (err) {
                console.error('Error initializing context:', err);
                setIsContextReady(true);
            }
        };

        initializeContext();
    }, []);

    const fetchUserInfoAfterConnect = async () => {
        try {
            let url = `${PRISMAX_BACKEND_URL}/api/get-users`;
            let headers = {
                'Content-Type': 'application/json'
            };

            if (userEmail) {
                if (!gatewayToken) {
                    console.error('No token found for email authentication');
                    return;
                }

                const params = new URLSearchParams();
                params.append('email', userEmail);
                url += `?${params}`;

                headers['Authorization'] = `Bearer ${gatewayToken}`;
                // todo could be issue here
                // } else if (walletAddress) {
                //     const params = new URLSearchParams();
                //     params.append('wallet_address', walletAddress);
                //     url += `?${params}`;
                // }
            } else {
                console.error('No email  available');
                return;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            if (response.ok) {
                const result = await response.json();

                if (result.success && result.data) {
                    if (result.data.user_class) {
                        localStorage.setItem('userClass', result.data.user_class);
                        setUserClass(result.data.user_class);
                    }
                    if (result.data.hash_code) {
                        localStorage.setItem('gatewayToken', result.data.hash_code);
                        setGatewayToken(result.data.hash_code)
                    }
                    if (result.data.userid) {
                        localStorage.setItem('userId', result.data.userid);
                        console.log(typeof result.data.userid)
                        setUserId(result.data.userid)
                    }
                }
            } else {
                const errorData = await response.json();
                console.error('Failed to fetch user info:', errorData.msg || 'Unknown error');
            }
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    };

    const handleDailyLoginPoints = async () => {
        try {
            const requestBody = {};
            if (userEmail) {
                requestBody.email = userEmail;
            } else if (walletAddress) {
                requestBody.wallet_address = walletAddress;
            } else {
                return;
            }
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');

            requestBody.user_local_date = `${year}-${month}-${day}`;
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/daily-login-points`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data.is_first_time) {
                    setShowFirstTimeModal(true);
                    setPointsRefreshTrigger(prev => prev + 1);
                } else if (data.success && data.data.points_awarded_today > 0) {
                    showNotification('success', `Welcome! You earned ${data.data.points_awarded_today} points today!`);
                    setPointsRefreshTrigger(prev => prev + 1);
                }
            }
        } catch (error) {
            console.error('Error handling daily login points:', error);
        }
    };

    useEffect(() => {
        if (userEmail) {
            fetchUserInfoAfterConnect();
            handleDailyLoginPoints();
        } else if (walletAddress) {
            handleDailyLoginPoints();
        }
    }, [walletAddress, userEmail]);

    useEffect(() => {
        if (walletAddress) {
            localStorage.setItem('walletAddress', walletAddress);
        } else {
            localStorage.removeItem('walletAddress');
        }
    }, [walletAddress]);

    useEffect(() => {
        if (userEmail) {
            localStorage.setItem('userEmail', userEmail);
        } else {
            localStorage.removeItem('userEmail');
        }
    }, [userEmail]);

    const clearUserData = useCallback(() => {
        //  reset in-memory state
        setWalletAddress('');
        setUserEmail('');
        setUserClass('Explorer Member');
        setUsdtBalance(0);
        setUsdcBalance(0);
        setGatewayToken('');
        setSessionUrlKey('');
        setAmount('');
        setTransactionTrigger(0);
        setIsProcessing(false);
        setUserId(null)

    }, []);


    return (
        <SwapMainAppContext.Provider
            value={{
                isContextReady,
                notification,
                showNotification,
                walletAddress,
                setWalletAddress,
                selectedToken,
                setSelectedToken,
                amount,
                setAmount,
                usdtTokenAddress,
                usdcTokenAddress,
                prismaxRecevingAddress,
                transactionTrigger,
                setTransactionTrigger,
                onMembershipPaymentSuccess,
                setOnMembershipPaymentSuccess,
                userClass,
                setUserClass,
                userEmail,
                setUserEmail,
                isProcessing,
                setIsProcessing,
                fetchUserInfoAfterConnect,
                setUsdtBalance,
                usdtBalance,
                usdcBalance,
                setUsdcBalance,
                showFirstTimeModal,
                pointsRefreshTrigger,
                gatewayToken,   //match to user
                setGatewayToken,
                setSessionUrlKey, //used to decrypt url to control robot
                sessionUrlKey,
                clearUserData,
                shrinkLeftPanel,
                setShrinkLeftPanel,
                setShowPanel,
                showPanel,
                userId,
                setUserId
            }}
        >
            {children}
            <FirstTimeModal
                isOpen={showFirstTimeModal}
                onClose={() => setShowFirstTimeModal(false)}
            />
        </SwapMainAppContext.Provider>
    );
}